import React from "react";
import { Link } from "react-router-dom";
import useWaitlistModal from "./useWaitlistModal";
import Header from "./Header";
import Footer from "./Footer";
import WaitlistModal from "./WaitlistModal";
import HeroPage from "./assets/HeroPage1.png";
import Feature1 from "./assets/Feature1.png";
import Feature2 from "./assets/Feature2.png";
import Feature3 from "./assets/Feature3.png";
import Feature4 from "./assets/Feature4.png";
import Feature5 from "./assets/Feature5.png";
import salesforcelogo from "./assets/salesforcelogo.png";
import hubspotlogo from "./assets/hubspotlogo.png";
import zohologo from "./assets/zohologo.png";
import outlooklogo from "./assets/outlooklogo.png";
import calendarlogo from "./assets/google-calendar-logo.png";
import gmaillogo from "./assets/gmaillogo.png";
import documentlogo from "./assets/documentlogo.png";
import transcriptlogo from "./assets/transcriptionlogo.png";
import zoomlogo from "./assets/zoomlogo.png";
import meetlogo from "./assets/meetlogo.png";
import teamslogo from "./assets/teamslogo.png";
import ciscologo from "./assets/ciscologo.png";

// Product Page Component
const ProductPage = () => {
  const {
    showWaitlistModal,
    formData,
    isSubmitting,
    isSubmitted,
    formError,
    openWaitlistModal,
    closeWaitlistModal,
    handleInputChange,
    handleSubmit,
  } = useWaitlistModal();

  return (
    <div className="min-h-screen bg-gray-100">
      <Header onOpenWaitlist={openWaitlistModal} />

      {/* Main Content */}
      <main className="bg-gray-100">
        {/* Hero Section */}
        <section className="h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto w-full flex flex-col items-center justify-center h-full gap-6">
            {/* Text Content */}
            <div className="text-center flex-shrink-0">
              <div className="text-xs leading-tight font-black uppercase tracking-widest text-sky-500 mb-2 sm:mb-3">
                Your meetings, notes, calendar, and CRM finally talk to each other
              </div>
              <h1 className="text-3xl sm:text-4xl leading-tight tracking-tight font-black text-jindo-orange mb-2 sm:mb-3">
                Build the Memory
              </h1>
              <p className="text-sm md:text-base leading-relaxed text-slate-600 max-w-4xl mx-auto">
                Jindo connects meetings, notes, calendar, and CRM into shared customer memory, helping GTM teams to understand what changed, what matters, and what to do next to win the deal.
              </p>
            </div>

            {/* Image */}
            <div className="max-w-6xl flex items-center justify-center min-h-0 border border-slate-300">
              <img
                src={HeroPage}
                alt="Jindo Product Interface"
                className="block w-full h-full object-contain"
              />
            </div>
          </div>
        </section>

        {/* Feature Sections */}
        <div className="bg-white">
          {/* Feature 1 */}
          <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <ProductFeature
                eyebrow="Jindo builds the context"
                heading="Focus on the client"
                subheading="Engage fully, build trust, and nurture the relationship while Jindo quietly captures and synthesizesthe client context that matters, all without distracting from the conversation or requiring a visible bot."
                supportingLine="The context can carry itself forward"
                image={Feature1}
                imageOnRight={true}
              />
            </div>
          </div>

          {/* Feature 2 */}
          <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
            <div className="max-w-7xl mx-auto">
              <ProductFeature
                eyebrow="Every interaction builds on the last"
                heading="Carry every interaction forward"
                subheading="Jindo brings together the full account story, from your latest to past interactions, giving you the context to move every client relationship forward."
                supportingLine="Yesterday's interactions can sharpen today's"
                image={Feature2}
                imageOnRight={false}
              />
            </div>
          </div>

          {/* Feature 3 */}
          <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-sky-50/50">
            <div className="max-w-7xl mx-auto">
              <ProductFeature
                eyebrow="Small stuff, big deals"
                heading="Use the details that count"
                subheading="You never lose the detail that closes the deal, because Jindo turns it into a nugget linked back to the interaction it came from."
                supportingLine="The right detail can change the deal"
                image={Feature3}
                imageOnRight={true}
              />
            </div>
          </div>

          {/* Feature 4 */}
          <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-jindo-orange-50/30">
            <div className="max-w-7xl mx-auto">
              <ProductFeature
                eyebrow="Stop scrolling through months of history"
                heading="Ask Jindo"
                subheading="Ask Jindo connects the dots across your client customer interactions, helping you synthesize the right answer, uncover hidden blockers, and move the opportunity forward."
                supportingLine="The right question can unlock the next move"
                image={Feature4}
                imageOnRight={false}
              />
            </div>
          </div>

          {/* Feature 5 */}
          <div className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
            <div className="max-w-7xl mx-auto">
              <ProductFeature
                eyebrow="Five prospects, one objection, one pattern"
                heading="Spot the pattern"
                subheading="You catch the objection before it costs you the deal, because Jindo flags it the moment the same concern shows up across multiple accounts."
                supportingLine="The pattern can win you many deals"
                image={Feature5}
                imageOnRight={true}
              />
            </div>
          </div>
        </div>

        {/* Integrations Section */}
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gray-100 border-t border-b border-slate-900/10">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 sm:mb-10 lg:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl leading-tight tracking-tight font-black mb-4 sm:mb-5 text-slate-900">
                Works where customer context already lives
              </h2>
              <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-slate-600 max-w-4xl">
                Jindo is designed to sit above the tools and channels your GTM
                team already uses.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 lg:gap-6">
              <IntegrationBox
                title="CRM"
                integrations={[
                  { name: "Salesforce", logo: salesforcelogo },
                  { name: "HubSpot", logo: hubspotlogo },
                  { name: "Zoho", logo: zohologo },
                ]}
              />
              <IntegrationBox
                title="Email"
                integrations={[
                  { name: "Gmail", logo: gmaillogo },
                  { name: "Outlook", logo: outlooklogo },
                ]}
              />
              <IntegrationBox
                title="Calendar"
                integrations={[
                  { name: "Google Calendar", logo: calendarlogo },
                  { name: "Outlook Calendar", logo: outlooklogo },
                ]}
              />
              <IntegrationBox
                title="Meetings"
                integrations={[
                  { name: "Zoom", logo: zoomlogo },
                  { name: "Google Meet", logo: meetlogo },
                  { name: "MS Teams", logo: teamslogo },
                  { name: "Cisco Webex", logo: ciscologo },
                ]}
              />
              <IntegrationBox
                title="More"
                integrations={[
                  { name: "Documents", logo: documentlogo },
                  { name: "Transcripts", logo: transcriptlogo },
                ]}
              />
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-900/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl leading-tight tracking-tight font-black mb-4 sm:mb-6 text-slate-900">
              Ready to stop losing customer context?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-slate-600 mb-6 sm:mb-8 lg:mb-10">
              Join the waitlist and benefit from Jindo's GTM intelligence.
            </p>
            <button
              onClick={openWaitlistModal}
              className="inline-flex items-center justify-center rounded-full border bg-black text-jindo-orange-600 px-6 sm:px-8 py-3 sm:py-3.5 lg:py-4 font-black text-sm sm:text-base lg:text-lg leading-none shadow-lg shadow-jindo-orange/20 hover:-translate-y-0.5 transition-transform"
            >
              Join the waitlist
            </button>
          </div>
        </section>
      </main>

      <Footer />

      {/* Waitlist Modal */}
      <WaitlistModal
        showWaitlistModal={showWaitlistModal}
        closeWaitlistModal={closeWaitlistModal}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isSubmitted={isSubmitted}
        formError={formError}
      />
    </div>
  );
};

// Helper Components
const IntegrationBox = ({ title, integrations }) => {
  return (
    <article className="min-h-[240px] sm:min-h-[260px] border border-slate-900/10 rounded-2xl sm:rounded-3xl p-5 sm:p-6 bg-jindo-orange-50/30 shadow-xl shadow-slate-900/5">
      <h3 className="text-lg sm:text-xl lg:text-[22px] tracking-tight font-black m-0 mb-4 sm:mb-5 text-slate-900">
        {title}
      </h3>
      <div className="grid gap-3 sm:gap-3.5">
        {integrations.map((integration, index) => (
          <div
            key={index}
            className="flex items-center gap-2 sm:gap-3 rounded-xl sm:rounded-2xl bg-white border border-slate-900/10 p-2.5 sm:p-3 min-h-[60px] sm:min-h-[70px] shadow-md shadow-slate-900/5"
          >
            <img
              src={integration.logo}
              alt={`${integration.name} logo`}
              className="w-10 h-10 sm:w-11 sm:h-11 min-w-[2.5rem] sm:min-w-[2.75rem] object-contain rounded-lg sm:rounded-xl bg-white p-1 sm:p-1.5 flex-shrink-0"
            />
            <div>
              <strong className="text-sm sm:text-[15px] text-slate-900">
                {integration.name}
              </strong>
            </div>
          </div>
        ))}
      </div>
    </article>
  );
};

const ProductFeature = ({
  eyebrow,
  heading,
  subheading,
  supportingLine,
  image,
  imageOnRight,
}) => {
  return (
    <div
      className={`grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 items-center ${imageOnRight ? "" : "lg:grid-flow-dense"}`}
    >
      {/* Text Content */}
      <div
        className={`${imageOnRight ? "" : "lg:col-start-2"} space-y-3 sm:space-y-4`}
      >
        <div className="text-xs sm:text-sm leading-tight font-black uppercase tracking-widest text-jindo-orange">
          {eyebrow}
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl leading-tight tracking-tight font-black text-slate-900">
          {heading}
        </h2>
        <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-slate-900">
          {subheading}
        </p>
        <p className="text-base sm:text-lg lg:text-xl leading-relaxed font-semibold text-sky-500 italic">
          {supportingLine}
        </p>
      </div>

      {/* Image */}
      <div
        className={`relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10 ${imageOnRight ? "" : "lg:col-start-1 lg:row-start-1"}`}
      >
        <img src={image} alt={heading} className="block w-full h-auto" />
      </div>
    </div>
  );
};

export default ProductPage;
